<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';
import SalesPropertyWarningAlert from './SalesPropertyWarningAlert.vue';

const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const secondaryDismissed = ref(false);
const tenthDismissed = ref(false);
const secondaryEmpty = computed(() => !props.profile.clausula_segunda);
const tenthEmpty = computed(() => !props.profile.clausula_decima);
const showSecondaryWarning = computed(
  () => secondaryEmpty.value && !secondaryDismissed.value
);
const showTenthWarning = computed(
  () => tenthEmpty.value && !tenthDismissed.value
);

watch(
  () => props.profile.clausula_segunda,
  (value, previousValue) => {
    if (!value && previousValue) secondaryDismissed.value = false;
  }
);
watch(
  () => props.profile.clausula_decima,
  (value, previousValue) => {
    if (!value && previousValue) tenthDismissed.value = false;
  }
);

const update = (field, value) => emit('update', field, value);
</script>

<template>
  <LaisCard
    density="flush"
    class="self-start !border-0 !shadow-sm"
    data-test="sales-property-clauses-section"
  >
    <div class="grid p-8 font-roboto">
      <h2
        class="mb-2 font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
      >
        {{ t('SALES_PROPERTY.CLAUSES.TITLE') }}
      </h2>
      <div class="mb-[61px] grid" data-test="secondary-clause-block">
        <p class="mb-11 text-[19.5px] leading-6 text-lais-text-body">
          {{ t('SALES_PROPERTY.CLAUSES.SECONDARY_TEXT') }}
          <strong>{{ t('SALES_PROPERTY.CLAUSES.SECONDARY_BOLD') }}</strong>
        </p>
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.clausula_segunda)"
          :label="t('SALES_PROPERTY.CLAUSES.SECONDARY')"
          :error="errors.clausula_segunda"
          :show-success="false"
          variant="textarea"
          class="!min-h-[118px]"
        >
          <textarea
            :value="props.profile.clausula_segunda"
            :placeholder="t('SALES_PROPERTY.CLAUSES.SECONDARY_PLACEHOLDER')"
            rows="2"
            :class="
              props.profile.clausula_segunda
                ? '!m-0 !h-[118px] !w-full !resize-y !rounded-[12px_12px_0_12px] !border-0 !bg-[#EDF8F7] !px-4 !pt-[46px] !pb-3 !text-[19.2px] !font-bold !leading-[29px] !text-[#009F94] !outline-none !placeholder:text-[#A7B7BE]'
                : '!m-0 !h-[118px] !w-full !resize-y !rounded-[12px_12px_0_12px] !border !border-[#E0E7EB] !bg-white !px-3 !pt-[46px] !pb-3 !text-[19.2px] !font-normal !leading-[29px] !text-[#5D696F] !outline-none !placeholder:text-[#A7B7BE]'
            "
            @input="update('clausula_segunda', $event.target.value)"
          />
        </SalesPropertyReferenceField>
        <SalesPropertyWarningAlert
          v-if="showSecondaryWarning"
          test-id="secondary-warning"
          :dismiss-label="t('SALES_PROPERTY.CLAUSES.DISMISS_WARNING')"
          class="mt-[10px]"
          @dismiss="secondaryDismissed = true"
        >
          {{ t('SALES_PROPERTY.CLAUSES.SECONDARY_WARNING_PREFIX') }}
          <strong>{{
            t('SALES_PROPERTY.CLAUSES.SECONDARY_WARNING_BOLD')
          }}</strong>
        </SalesPropertyWarningAlert>
      </div>
      <div class="mb-[61px] grid" data-test="tenth-clause-block">
        <p class="mb-11 text-[19.5px] leading-6 text-lais-text-body">
          {{ t('SALES_PROPERTY.CLAUSES.TENTH_TEXT') }}
          <strong>{{ t('SALES_PROPERTY.CLAUSES.TENTH_BOLD') }}</strong>
        </p>
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.clausula_decima)"
          :label="t('SALES_PROPERTY.CLAUSES.TENTH')"
          :show-success="false"
          variant="textarea"
          class="!min-h-[118px]"
        >
          <textarea
            :value="props.profile.clausula_decima"
            :placeholder="t('SALES_PROPERTY.CLAUSES.TENTH_PLACEHOLDER')"
            rows="2"
            :class="
              props.profile.clausula_decima
                ? '!m-0 !h-[118px] !w-full !resize-y !rounded-[12px_12px_0_12px] !border-0 !bg-[#EDF8F7] !px-4 !pt-[46px] !pb-3 !text-[19.2px] !font-bold !leading-[29px] !text-[#009F94] !outline-none !placeholder:text-[#A7B7BE]'
                : '!m-0 !h-[118px] !w-full !resize-y !rounded-[12px_12px_0_12px] !border !border-[#E0E7EB] !bg-white !px-3 !pt-[46px] !pb-3 !text-[19.2px] !font-normal !leading-[29px] !text-[#5D696F] !outline-none !placeholder:text-[#A7B7BE]'
            "
            @input="update('clausula_decima', $event.target.value)"
          />
        </SalesPropertyReferenceField>
        <SalesPropertyWarningAlert
          v-if="showTenthWarning"
          test-id="tenth-warning"
          :dismiss-label="t('SALES_PROPERTY.CLAUSES.DISMISS_WARNING')"
          class="mt-[10px]"
          @dismiss="tenthDismissed = true"
        >
          {{ t('SALES_PROPERTY.CLAUSES.TENTH_WARNING_PREFIX') }}
          <strong>{{ t('SALES_PROPERTY.CLAUSES.TENTH_WARNING_BOLD') }}</strong>
          {{ t('SALES_PROPERTY.CLAUSES.TENTH_WARNING_SUFFIX') }}
        </SalesPropertyWarningAlert>
      </div>
      <slot name="footer" />
    </div>
  </LaisCard>
</template>
